import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Gavel, IndianRupee, Loader2, Trophy, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BidPanel = ({ societyId, bidType, candidates, idKey, statusPath, onWinnerSelected }) => {
  const [bids, setBids] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [form, setForm] = useState({ bid_amount: "", timeline_months: "", bid_notes: "" });
  const [saving, setSaving] = useState(false);

  const fetchBids = useCallback(async () => {
    try {
      const res = await fetch(`${API}/societies/${societyId}/bids/compare?bid_type=${bidType}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setBids(data.bids);
      }
    } catch {
      // ignore
    }
  }, [societyId, bidType]);

  useEffect(() => {
    if (societyId) fetchBids();
  }, [societyId, fetchBids]);

  const submitBid = async (e) => {
    e.preventDefault();
    if (!target || !form.bid_amount) {
      toast.error("Select a candidate and enter bid amount");
      return;
    }
    setSaving(true);
    try {
      const path = bidType === "pmc" ? "pmc" : "builders";
      const res = await fetch(`${API}/societies/${societyId}/${path}/${target}/bid`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bid_amount: parseFloat(form.bid_amount),
          timeline_months: form.timeline_months ? parseInt(form.timeline_months) : null,
          bid_notes: form.bid_notes || null
        }),
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Bid recorded");
        setDialogOpen(false);
        setForm({ bid_amount: "", timeline_months: "", bid_notes: "" });
        setTarget("");
        fetchBids();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to record bid");
      }
    } catch {
      toast.error("Failed to record bid");
    } finally {
      setSaving(false);
    }
  };

  const selectWinner = async (id) => {
    try {
      const res = await fetch(`${API}/societies/${societyId}/${statusPath}/${id}/status?status=selected`, {
        method: "PUT",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Winning bid selected!");
        fetchBids();
        onWinnerSelected && onWinnerSelected();
      } else {
        toast.error("Failed to select winner");
      }
    } catch {
      toast.error("Failed to select winner");
    }
  };

  return (
    <Card className="border-blue-200" data-testid={`bid-panel-${bidType}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gavel className="w-5 h-5 text-blue-700" />
              {bidType === "pmc" ? "PMC Bidding" : "Work Execution Bidding"}
            </CardTitle>
            <CardDescription>
              Record quotes from {bidType === "pmc" ? "consultants" : "builders"}, compare side-by-side & select the winner
            </CardDescription>
          </div>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => setDialogOpen(true)}
            disabled={candidates.length === 0}
            data-testid={`record-bid-btn-${bidType}`}
          >
            <IndianRupee className="w-4 h-4 mr-1" />
            Record Bid
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {bids.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">
            No bids recorded yet. {candidates.length === 0 ? `Add ${bidType === "pmc" ? "PMCs" : "builders"} first, then record their quotes.` : "Click 'Record Bid' to add quotes for comparison."}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rank</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Bid Amount</TableHead>
                <TableHead>Timeline</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bids.map((bid) => (
                <TableRow key={bid.id} className={bid.is_lowest ? "bg-emerald-50/60" : ""} data-testid={`bid-row-${bid.id}`}>
                  <TableCell>
                    {bid.is_lowest ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <Trophy className="w-3 h-3 mr-1" />L1
                      </Badge>
                    ) : (
                      <span className="text-slate-500 text-sm">L{bid.rank}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{bid.company}</TableCell>
                  <TableCell className="font-semibold">₹{bid.bid_amount.toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-sm">{bid.timeline_months ? `${bid.timeline_months} mo` : "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{bid.notes || "-"}</TableCell>
                  <TableCell>
                    {bid.status === "selected" ? (
                      <Badge className="bg-emerald-600 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" />Winner
                      </Badge>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => selectWinner(bid.id)} data-testid={`select-winner-${bid.id}`}>
                        Select
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record {bidType === "pmc" ? "PMC" : "Execution"} Bid</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitBid} className="space-y-4">
            <div>
              <Label>Candidate *</Label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                required
                data-testid="bid-candidate-select"
              >
                <option value="">Select {bidType === "pmc" ? "PMC" : "builder"}...</option>
                {candidates.map((c) => (
                  <option key={c[idKey]} value={c[idKey]}>{c.company}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Bid Amount (₹) *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.bid_amount}
                onChange={(e) => setForm({ ...form, bid_amount: e.target.value })}
                placeholder={bidType === "pmc" ? "e.g., 500000" : "e.g., 50000000"}
                required
                data-testid="bid-amount-input"
              />
            </div>
            <div>
              <Label>Timeline (months)</Label>
              <Input
                type="number"
                value={form.timeline_months}
                onChange={(e) => setForm({ ...form, timeline_months: e.target.value })}
                placeholder="e.g., 36"
                data-testid="bid-timeline-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.bid_notes}
                onChange={(e) => setForm({ ...form, bid_notes: e.target.value })}
                placeholder="Scope, terms, inclusions..."
                rows={2}
                data-testid="bid-notes-input"
              />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="submit-bid-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gavel className="w-4 h-4 mr-2" />}
              Save Bid
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
